import { useMemo, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, ArrowCycle, Download } from '@factorialco/f0-react/icons/app'
import { ExpensablesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import type { Group } from 'modules/expenses/hooks/useGroup'
import { useExportPdf } from 'modules/expenses/hooks/useGroup'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'
import { Modals as SyncModal } from 'modules/integrations/components/SyncButton/Modals'
import { useSync } from 'modules/integrations/hooks/useSync'

import { useExpensesBulkActions } from './bulkActions'
import { mapFiltersToVariables } from './filters'
import { useExpenseItemActions } from './itemActions'
import { useDataCollectionConfig } from './useDataCollectionConfig'
import { useExpensablesStatusCounts } from './useExpensablesStatusCounts'
import { useExpensesDataCollection } from './useExpensesDataCollection'
import { useExpensesFilters } from './useExpensesFilters'

interface Props {
  companyId: number
  canSeeTransactions: boolean
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  back: { href: string }
  onAddUsers?: () => void
  group?: Group
}

export const ExpensesDataCollection = ({
  companyId,
  canSeeTransactions,
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  back,
  onAddUsers,
  group,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const {
    access: { locale },
  } = useCurrentSession()

  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const { data: expensesData } = useExpenses()
  const { exportPdf, loading: exportingPdf } = useExportPdf()

  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const filterDefinitions = useExpensesFilters({ companyId })

  const statusCounts = useExpensablesStatusCounts({
    companyId,
    groupId: group?.id,
    includeManualDrafts: false,
    from: dateFilters.startDate,
    to: dateFilters.endDate,
  })

  const { navigationFilters: navigationFiltersDefinitions, presets: presetsDefinitions } =
    useDataCollectionConfig({ includeDraft: false, statusCounts })

  const { sortings, defaultSortings, columns } = useExpensesDataCollection({
    locale,
    customizationFields: expensesData.expenses.customizationFields ?? [],
    showGroupColumn: !group,
  })
  const expensePermissions = useMemo(
    () => ({
      canSyncWithPayroll: context.canSyncWithPayroll,
      canSyncWithBanking: context.canSyncWithBanking,
      canRestartApprovalFlow: expensesData.expenses._policies.expenseRestartApprovalFlow.can,
      canSetExpensableToPaid: expensesData.expenses._policies.expenseSetToPaid.can,
      section: context.section,
    }),
    [context, expensesData.expenses._policies]
  )

  const { bulkActions, handleBulkAction, Modals, handlers, modalActions } = useExpensesBulkActions({
    ...expensePermissions,
    currentPayrollPeriodOpen,
    availablePayrollCategories,
  })

  const { getItemActions } = useExpenseItemActions({
    ...expensePermissions,
    handlers,
    modalActions,
    navigationHandler: {
      navigate,
      navigation,
    },
    isViewingGroup: !!group,
  })

  const [showSyncModal, setShowSyncModal] = useState(false)
  const { syncEnabled, isSyncRunning, marketplaceIntegration } = useSync()
  const isSyncEnabled = syncEnabled && marketplaceIntegration

  const handleExportPDF = async () => {
    if (!group) return

    const handled = handleResult(await exportPdf(group.id, companyId), () => null)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.error'),
      })
    }
  }

  const dataSource = useGraphqlDataCollectionSource({
    document: ExpensablesDataCollectionDocument,
    mapData: (data) => data.expenses.expensablesConnection.nodes,

    mapVariables: ({ navigationFilters, search, sortings: mapSortings, filters }) => {
      const { status, employeeIds, grouped, filter } = mapFiltersToVariables(filters)
      return {
        companyId: companyId,
        companyIdString: companyId.toString(),
        sortOrder: mapSortings?.[0],
        onlyGrouped: grouped,
        includeGrouped: grouped !== false,
        includeManualDrafts: false,
        search,
        from: navigationFilters?.date?.value?.from,
        to: navigationFilters?.date?.value?.to,
        groupIds: group ? [group.id] : undefined,
        status,
        employeeIds,
        filter,
      }
    },
    paginationType: 'infinite-scroll',
    perPage: 100, // TODO: Change when F0 fixes the issue with the infinite scroll https://github.com/factorialco/f0/pull/2839
    mapPagination: (data) => ({
      total: data.expenses.expensablesConnection.totalCount ?? 0,
      cursor: data.expenses.expensablesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.expensablesConnection.pageInfo.hasNextPage ?? false,
    }),

    navigationFilters: navigationFiltersDefinitions,
    search: {
      enabled: true,
    },
    filters: filterDefinitions,
    presets: presetsDefinitions,
    sortings,
    defaultSortings,
    itemUrl: ({ id }) => navigation.helpers.expensableDetail(id),
    itemActions: getItemActions,
    primaryActions: () => [
      ...(!isSyncEnabled && !group
        ? [
            {
              label: i18n.t('expenses_v2.buttons.export_expenses'),
              icon: Download,
              onClick: () => {
                navigate(navigation.exporter.fullPath())
              },
            },
          ]
        : []),
    ],
    secondaryActions: {
      expanded: group ? 0 : 1,
      actions: () => [
        ...(isSyncEnabled
          ? [
              {
                label: i18n.t('integrations.sync_action', {
                  integration_name: marketplaceIntegration.name,
                }),
                icon: ArrowCycle,
                loading: isSyncRunning,
                onClick: () => {
                  setShowSyncModal(true)
                },
              },
            ]
          : []),
        ...(group
          ? [
              {
                label: i18n.t('expenses_v2.buttons.export_to_pdf'),
                icon: Download,
                onClick: () => !exportingPdf && handleExportPDF(),
              },
            ]
          : []),
        ...(onAddUsers
          ? [
              {
                label: i18n.t('billing.subscription.plans.expenses.actions.add_users'),
                icon: Add,
                onClick: onAddUsers,
              },
            ]
          : []),
      ],
    },
    selectable: (item) => item.id,
    bulkActions,
  })

  return (
    <>
      <Modals />
      {showSyncModal && <SyncModal setShowModal={setShowSyncModal} />}
      <Outlet context={{ ...context, back, canSeeTransactions, dataCollectionEnabled: true }} />
      <Box paddingX="s12" style={{ height: '100%', overflow: 'auto' }}>
        <OneDataCollection
          id="expenses/data-collection/v2"
          storage={{
            features: ['filters', 'sortings', 'search'],
          }}
          fullHeight
          emptyStates={{
            'no-data': {
              title: i18n.t('expenses_v2.list.empty_state.no_expenses'),
              description: '',
            },
          }}
          source={dataSource}
          onBulkAction={handleBulkAction}
          onStateChange={(state) => {
            // Sync date filters with page-level state
            const { from, to } = state.navigationFilters?.date?.value || {}
            if (from && to) {
              if (
                dateFilters.startDate.getTime() !== from.getTime() ||
                dateFilters.endDate.getTime() !== to.getTime()
              ) {
                setPageDateFilters(from, to)
              }
            }
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                allowColumnHiding: true,
                allowColumnReordering: true,
                columns,
              },
            },
          ]}
        />
      </Box>
    </>
  )
}
